import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, type GateView, type ProjectStatusView } from '../lib/api';
import { formatStatus } from '../lib/format';

/**
 * The hold panel — §4.5, CALM ruling 2026-09-07.
 *
 * §4.5's exit gate for `on_hold` reads "Cause resolved; cause and resume date
 * recorded". Until migration 0008 there was nowhere to record either, so the
 * gate reported `not_evaluable` and a held project could leave the hold with
 * nothing written down. This panel is where the two facts are captured.
 *
 * The cause is captured on the way in, on the same `reason` field that a
 * cancellation and a termination already use, because §4.5 asks for one
 * written cause and one mechanism is enough. The resume date is captured
 * afterwards, on the project, and is the thing the exit gate waits on.
 *
 * The start date appears here as text and never as an input. It is written by
 * the server from the database's own date on the transition into `on_hold`; a
 * gate whose evidence the caller could type is not a gate.
 *
 * §0.1: every surface here is white or #F4F6FA, so no gold appears anywhere in
 * this file. Blocked reads in danger red, satisfied in success green, both of
 * which are legal on light.
 */

const OUTCOME_TONE: Record<GateView['outcome'], string> = {
  satisfied: 'border-success text-success',
  blocked: 'border-danger text-danger',
  not_evaluable: 'border-text-secondary text-text-secondary',
  advisory: 'border-warning text-warning',
};

const HOLD_GATE = 'hold_cause_resolved';

function GateLine({ gate }: { gate: GateView }): JSX.Element {
  return (
    <div className="rounded-card border border-border bg-surface p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="numeric text-numeric text-text-primary">{gate.id}</span>
        <span
          className={`inline-flex items-center rounded-pill border px-2 py-0.5 text-micro uppercase ${
            OUTCOME_TONE[gate.outcome]
          }`}
        >
          {gate.outcome.replace(/_/g, ' ')}
        </span>
      </div>
      <p className="mt-1 text-body text-text-secondary">{gate.detail}</p>
    </div>
  );
}

export function HoldPanel({ projectId }: { projectId: string }): JSX.Element {
  const [view, setView] = useState<ProjectStatusView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cause, setCause] = useState('');
  const [resumeOn, setResumeOn] = useState('');
  const [resumeTo, setResumeTo] = useState('');

  const load = useCallback(async () => {
    try {
      const next = await api.projectStatus(projectId);
      setView(next);
      setResumeOn(next.hold.resumeOn ?? '');
      setError(null);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.body.message : 'The status could not be read.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Every write reloads from the server: the panel reports the row, not hope. */
  const run = useCallback(
    async (work: () => Promise<string>) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        setNotice(await work());
        await load();
      } catch (err) {
        setError(err instanceof ApiFailure ? err.body.message : 'The change was refused.');
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  if (error && !view) {
    return (
      <div className="card rounded-card border border-border bg-white p-4">
        <p className="text-body text-danger">{error}</p>
      </div>
    );
  }
  if (!view) {
    return (
      <div className="card rounded-card border border-border bg-white p-4">
        <p className="text-body text-text-secondary">Reading the status gates…</p>
      </div>
    );
  }

  const onHold = view.project.status === 'on_hold';
  const holdGate = view.exitGates.find((g) => g.id === HOLD_GATE);
  // §4.5 puts on_hold behind the same written-cause requirement as the two
  // terminal exception states; the server enforces it, and the button stays
  // disabled rather than letting the request be refused for something the
  // panel can already see.
  const holdRule = view.reachable.find((r) => r.status === 'on_hold');
  const resumeTargets = view.reachable.filter(
    (r) => r.status !== 'on_hold' && (r.allowed || r.overrideCapable),
  );

  return (
    <section
      aria-labelledby="hold-heading"
      className="card mb-4 rounded-card border border-border bg-white p-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="hold-heading" className="text-h2 font-semibold text-text-primary">
          Hold
        </h2>
        <span className="numeric text-numeric text-text-secondary">
          {view.project.code} · {formatStatus(view.project.status)}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-body">
        {[
          ['Cause', view.hold.cause],
          ['Held since', view.hold.startedOn],
          ['Resume on', view.hold.resumeOn],
        ].map(([term, value]) => (
          <div key={term as string} className="contents">
            <dt className="micro-label pt-1 text-text-secondary">{term}</dt>
            <dd className={term === 'Cause' ? 'text-text-primary' : 'numeric text-numeric'}>
              {(value as string | null) ?? '—'}
            </dd>
          </div>
        ))}
      </dl>

      {holdGate ? (
        <div className="mt-3">
          <GateLine gate={holdGate} />
        </div>
      ) : null}

      {notice ? (
        <p role="status" className="mt-3 text-body text-success">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-3 text-body text-danger">
          {error}
        </p>
      ) : null}

      {onHold ? (
        <div className="mt-4 grid gap-4">
          <div>
            <label
              htmlFor="hold-resume-on"
              className="micro-label block text-text-secondary"
            >
              Resume date
            </label>
            <p className="mb-1 text-body text-text-secondary">
              §4.5 will not let this project leave the hold until a resume date is recorded.
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                id="hold-resume-on"
                type="date"
                value={resumeOn}
                onChange={(e) => setResumeOn(e.target.value)}
                className="min-h-touch rounded-card border border-border bg-white px-3 py-2 text-body text-text-primary"
              />
              <button
                type="button"
                disabled={busy || resumeOn === '' || resumeOn === view.hold.resumeOn}
                onClick={() =>
                  void run(async () => {
                    await api.recordHold(projectId, { resumeOn });
                    return `Resume date recorded as ${resumeOn}.`;
                  })
                }
                className="btn-primary min-h-touch rounded-card px-4 text-body font-medium disabled:opacity-40"
              >
                Record resume date
              </button>
            </div>
          </div>

          {view.hold.cause === null ? (
            <div>
              <label htmlFor="hold-cause-late" className="micro-label block text-text-secondary">
                Hold cause
              </label>
              <p className="mb-1 text-body text-text-secondary">
                This hold was placed before a cause could be recorded. Write what stopped the work.
              </p>
              <textarea
                id="hold-cause-late"
                rows={2}
                value={cause}
                onChange={(e) => setCause(e.target.value)}
                className="w-full rounded-card border border-border bg-white px-3 py-2 text-body text-text-primary"
              />
              <button
                type="button"
                disabled={busy || cause.trim().length < 3}
                onClick={() =>
                  void run(async () => {
                    await api.recordHold(projectId, { holdCause: cause.trim() });
                    setCause('');
                    return 'Hold cause recorded.';
                  })
                }
                className="btn-primary mt-2 min-h-touch rounded-card px-4 text-body font-medium disabled:opacity-40"
              >
                Record cause
              </button>
            </div>
          ) : null}

          <div>
            <label htmlFor="hold-resume-to" className="micro-label block text-text-secondary">
              Resume work as
            </label>
            <div className="mt-1 flex flex-wrap gap-2">
              <select
                id="hold-resume-to"
                value={resumeTo}
                onChange={(e) => setResumeTo(e.target.value)}
                className="min-h-touch rounded-card border border-border bg-white px-3 py-2 text-body text-text-primary"
              >
                <option value="">Choose a status…</option>
                {resumeTargets.map((r) => (
                  <option key={r.status} value={r.status}>
                    {formatStatus(r.status)}
                    {r.allowed ? '' : ' — blocked'}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={busy || resumeTo === ''}
                onClick={() =>
                  void run(async () => {
                    const done = await api.transitionProject(projectId, { to: resumeTo });
                    setResumeTo('');
                    return `Moved to ${formatStatus(done.project.status)}.`;
                  })
                }
                className="btn-secondary min-h-touch rounded-card px-4 text-body font-medium disabled:opacity-40"
              >
                Leave hold
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <label htmlFor="hold-cause" className="micro-label block text-text-secondary">
            Hold cause
          </label>
          <p className="mb-1 text-body text-text-secondary">
            A hold requires a written cause. It is recorded on the project and on the activity log,
            and the hold cannot be left until a resume date joins it.
          </p>
          <textarea
            id="hold-cause"
            rows={2}
            value={cause}
            onChange={(e) => setCause(e.target.value)}
            className="w-full rounded-card border border-border bg-white px-3 py-2 text-body text-text-primary"
          />
          <button
            type="button"
            disabled={busy || cause.trim().length < 3 || holdRule === undefined}
            onClick={() =>
              void run(async () => {
                const done = await api.transitionProject(projectId, {
                  to: 'on_hold',
                  reason: cause.trim(),
                });
                setCause('');
                return `On hold since ${done.hold.startedOn ?? 'today'}.`;
              })
            }
            className="btn-destructive mt-2 min-h-touch rounded-card px-4 text-body font-medium disabled:opacity-40"
          >
            Place on hold
          </button>
        </div>
      )}
    </section>
  );
}
